import React, { useState, useEffect } from 'react';
import { X, Search, UserPlus, CheckCircle, Loader, Users } from 'lucide-react';
import { aiCertificationAPI } from '../../services/api';

const ImportFromAccountsModal = ({ isOpen, onClose, onSuccess }) => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (isOpen) loadAccounts();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const debounce = setTimeout(() => loadAccounts(), 300);
    return () => clearTimeout(debounce);
  }, [searchTerm]);

  const loadAccounts = async () => {
    setLoading(true);
    try {
      const res = await aiCertificationAPI.getAvailableAccounts(searchTerm);
      setAccounts(res.data.accounts || []);
    } catch (err) {
      console.error('Failed to load accounts:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAllAvailable = () => {
    const available = accounts.filter(a => !a.already_exists).map(a => a.account_id);
    if (selected.size === available.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(available));
    }
  };

  const handleImport = async () => {
    if (selected.size === 0) return;
    setImporting(true);
    try {
      const res = await aiCertificationAPI.importFromAccounts([...selected]);
      setResult(res.data);
      if (res.data.created > 0 && onSuccess) onSuccess();
    } catch (err) {
      setResult({ error: err.response?.data?.detail || 'Import failed' });
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setSelected(new Set());
    setResult(null);
    setSearchTerm('');
    onClose();
  };

  if (!isOpen) return null;

  const availableCount = accounts.filter(a => !a.already_exists).length;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="import-accounts-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Import from Investor Accounts</h3>
            <p className="text-xs text-gray-500 mt-0.5">Select accounts to add to AI Certification tracker</p>
          </div>
          <button onClick={handleClose} className="p-1 hover:bg-gray-100 rounded" data-testid="close-import-accounts"><X size={20} /></button>
        </div>

        <div className="p-4 border-b">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-2.5 text-gray-400" />
            <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search by account name or PAN..."
              className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm" data-testid="search-accounts-input" />
          </div>
          <div className="flex items-center justify-between mt-3 text-sm text-gray-500">
            <span>{availableCount} available to import ({accounts.length} total)</span>
            {availableCount > 0 && (
              <button onClick={selectAllAvailable} className="text-blue-600 hover:underline text-xs" data-testid="select-all-accounts">
                {selected.size === availableCount ? 'Deselect All' : 'Select All Available'}
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-2 min-h-0" style={{ maxHeight: '400px' }}>
          {loading ? (
            <div className="flex items-center justify-center py-8 text-gray-400"><Loader size={20} className="animate-spin mr-2" /> Loading accounts...</div>
          ) : accounts.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <Users size={32} className="mx-auto mb-2" />
              <p className="text-sm">No investor accounts found</p>
            </div>
          ) : (
            <div className="space-y-1">
              {accounts.map(acc => (
                <label
                  key={acc.account_id}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-colors ${
                    acc.already_exists ? 'opacity-50 cursor-not-allowed bg-gray-50' :
                    selected.has(acc.account_id) ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50 border border-transparent'
                  }`}
                  data-testid={`account-row-${acc.account_id}`}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(acc.account_id)}
                    disabled={acc.already_exists}
                    onChange={() => !acc.already_exists && toggleSelect(acc.account_id)}
                    className="rounded text-blue-600"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900 truncate">{acc.account_name}</span>
                      {acc.already_exists && <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] rounded-full font-medium">Already Added</span>}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                      {acc.pan && <span className="font-mono">{acc.pan}</span>}
                      {acc.type_of_investor && <span>{acc.type_of_investor}</span>}
                      {acc.city && <span>{acc.city}</span>}
                      {acc.email && <span>{acc.email}</span>}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Result */}
        {result && (
          <div className={`mx-5 my-2 p-3 rounded-lg text-sm ${result.error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`} data-testid="import-accounts-result">
            {result.error ? result.error : <><CheckCircle size={14} className="inline mr-1" />{result.message}</>}
          </div>
        )}

        <div className="flex justify-end gap-3 p-5 border-t">
          <button onClick={handleClose} className="px-4 py-2 border rounded-lg text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
          <button onClick={handleImport} disabled={selected.size === 0 || importing}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50" data-testid="import-accounts-btn">
            {importing ? <><Loader size={14} className="mr-2 animate-spin" /> Importing...</> : <><UserPlus size={14} className="mr-2" /> Import {selected.size > 0 ? `(${selected.size})` : ''}</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImportFromAccountsModal;
